'use client'
import {
  HAS_ALL_ACCESS,
  LOGGED_IN,
} from '@repo/web-ui/components/static-variants-renderer/utils/conditions'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import * as React from 'react'
import { useTranslations } from 'next-intl'

import { Typography } from '@repo/design-system-cf/components/typography'
import { CircleCheckmarkIcon } from '@repo/payload-kit/assets/icons'
import { LFS_URL } from '@web/utils/env'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'

export function JoinNowBanner({
  graphicsCount,
  fontsCount,
}: {
  graphicsCount: string
  fontsCount: string
}) {
  const t = useTranslations('pages.pod.join-now-banner')
  const t2 = useTranslations('pages.search')
  const { currency } = usePricesForUser()

  return (
    <InlineStaticVariant
      conditions={[LOGGED_IN, HAS_ALL_ACCESS]}
      getVariant={React.useCallback(
        ({ conditionsMet }) =>
          conditionsMet ? (
            <></>
          ) : (
            <div className="flex flex-col py-8 items-center w-full">
              <div className="gap-6 flex flex-col text-center items-center">
                <div className="flex flex-col gap-2">
                  <Typography asChild variant="cf-title-05">
                    <h2>{t('title')}</h2>
                  </Typography>
                  <Typography asChild variant="body-01" className="max-w-xl">
                    <p>
                      {t('subtitle', {
                        graphicsCount,
                        fontsCount,
                      })}
                    </p>
                  </Typography>
                </div>
                <div
                  role="img"
                  aria-label="background graphic"
                  className="px-4 py-8 w-full flex flex-col items-center max-w-3xl"
                  style={{
                    backgroundImage: `url(${LFS_URL}/web/banners/pod-bg.svg)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'top left',
                  }}
                >
                  <div className="flex flex-col gap-4 border border-[#e4e5ec] p-4 bg-cf-white rounded w-full max-w-xl">
                    <Typography asChild variant="cf-title-04">
                      <h3>{t('full_pod_access')}</h3>
                    </Typography>
                    <div className="grid sm:grid-cols-2 gap-1">
                      {Array.from({ length: 6 }, (_, i) => i + 1).map(item => (
                        <div key={item} className="flex items-center justify-start gap-1 text-left">
                          <CircleCheckmarkIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <Typography asChild variant="body-01" className="text-left">
                            <p>{t(`li_${item}`)}</p>
                          </Typography>
                        </div>
                      ))}
                    </div>
                    <Typography asChild variant="cf-title-01" className="uppercase py-2">
                      <p>{t('join_now')}</p>
                    </Typography>

                    <StartFreeTrialButton
                      label={t2('trial_product_card_variant.cta', {
                        currency: currency.symbol,
                      })}
                      entryPoint={PAYMENT_ENTRY_POINTS.POD_FOOTER}
                      className="text-14 text-cf-white bg-secondary-500 hover:bg-secondary-700 flex min-h-[40px] w-full items-center justify-center rounded-[2px] px-5 text-center font-semibold uppercase"
                    />
                  </div>
                </div>
              </div>
            </div>
          ),
        [fontsCount, graphicsCount, t, t2, currency.symbol],
      )}
    />
  )
}
